import { formatDateBR, formatDateOnly } from "@/utils/dateUtils";

/** Mudanças que a comparação entre duas buscas seguidas do mesmo perfil detecta. */
export const LINKEDIN_TRANSITION_KINDS = [
  "emprego_encerrado",
  "sem_emprego_atual",
  "open_to_work_ligado",
  "open_to_work_desligado",
  "freela_iniciado",
  "emprego_iniciado",
  "emprego_removido",
  "cargo_alterado",
  "contratando_ligado",
  "contratando_desligado",
  "headline_alterada",
  "localizacao_alterada",
] as const;

export type LinkedInTransitionKind = (typeof LINKEDIN_TRANSITION_KINDS)[number];

export const HOT_TRANSITION_KINDS: ReadonlySet<LinkedInTransitionKind> = new Set([
  "emprego_encerrado",
  "sem_emprego_atual",
  "open_to_work_ligado",
  "freela_iniciado",
]);

export interface LinkedInTransitionDetails {
  empresa?: string;
  cargo?: string;
  vinculo?: string;
  data_inicio?: string;
  data_fim?: string;
  primeira_observacao?: boolean;
  provedores_diferentes?: boolean;
}

export interface LinkedInTransition {
  kind: LinkedInTransitionKind;
  antes: string | boolean | null;
  depois: string | boolean | null;
  detalhes: LinkedInTransitionDetails;
}

const TRANSITION_LABEL: Record<LinkedInTransitionKind, string> = {
  emprego_encerrado: "Saiu do emprego",
  sem_emprego_atual: "Sem emprego atual",
  open_to_work_ligado: "Ligou o Open to Work",
  open_to_work_desligado: "Desligou o Open to Work",
  freela_iniciado: "Começou como freela",
  emprego_iniciado: "Novo emprego",
  emprego_removido: "Tirou uma experiência do perfil",
  cargo_alterado: "Mudou de cargo",
  contratando_ligado: "Está contratando",
  contratando_desligado: "Parou de contratar",
  headline_alterada: "Mudou o título do perfil",
  localizacao_alterada: "Mudou de cidade",
};

/** Eventos da timeline que carregam uma mudança vista no LinkedIn. */
export const TRANSITION_KIND_BY_EVENT_TYPE: Record<string, LinkedInTransitionKind> = {
  linkedin_emprego_encerrado: "emprego_encerrado",
  linkedin_sem_emprego_atual: "sem_emprego_atual",
  linkedin_open_to_work_ligado: "open_to_work_ligado",
  linkedin_open_to_work_desligado: "open_to_work_desligado",
  linkedin_freela_iniciado: "freela_iniciado",
  linkedin_emprego_iniciado: "emprego_iniciado",
};

export function isLinkedInTransitionKind(value: unknown): value is LinkedInTransitionKind {
  return LINKEDIN_TRANSITION_KINDS.includes(value as LinkedInTransitionKind);
}

export function transitionLabel(transition: Pick<LinkedInTransition, "kind" | "detalhes">): string {
  if (transition.kind === "open_to_work_ligado" && transition.detalhes.primeira_observacao) {
    return "Open to Work ligado";
  }
  return TRANSITION_LABEL[transition.kind];
}

const YEAR_MONTH = /^\d{4}-\d{2}$/;

/** Mês do LinkedIn ("YYYY-MM" ou só "YYYY") como "jun/2026" ou "2026". */
export function formatLinkedInMonth(value: string | undefined): string {
  if (!value) return "";
  return YEAR_MONTH.test(value) ? formatDateBR(`${value}-01`, "MMM/yyyy") : value;
}

function textValue(value: string | boolean | null): string {
  return typeof value === "string" ? value : "";
}

function roleAtCompany(details: LinkedInTransitionDetails): string {
  if (details.cargo && details.empresa) return `${details.cargo} em ${details.empresa}`;
  return details.cargo ?? details.empresa ?? "";
}

function withSuffix(base: string, suffix: string): string {
  if (!suffix) return base;
  return base ? `${base}, ${suffix}` : suffix;
}

/** Linha de detalhe da mudança: cargo, empresa, mês, ou o antes e o depois. */
export function transitionDetailLine(transition: LinkedInTransition): string {
  const details = transition.detalhes;
  const endMonth = formatLinkedInMonth(details.data_fim);
  const startMonth = formatLinkedInMonth(details.data_inicio);
  switch (transition.kind) {
    case "emprego_encerrado":
      return withSuffix(roleAtCompany(details), endMonth ? `até ${endMonth}` : "");
    case "sem_emprego_atual":
      return withSuffix(
        roleAtCompany(details) ? `Último: ${roleAtCompany(details)}` : "",
        endMonth ? `até ${endMonth}` : "",
      );
    case "freela_iniciado":
    case "emprego_iniciado":
      return withSuffix(roleAtCompany(details), startMonth ? `desde ${startMonth}` : "");
    case "emprego_removido":
      return roleAtCompany(details);
    case "cargo_alterado":
    case "localizacao_alterada": {
      const before = textValue(transition.antes);
      const after = textValue(transition.depois);
      if (before && after) return `${before} → ${after}`;
      return after || before;
    }
    case "headline_alterada":
      return textValue(transition.depois);
    default:
      return "";
  }
}

/** Quando foi visto: o dia da busca e, havendo busca anterior, a janela entre as duas. */
export function transitionWindow(observedAt: string, observedBeforeAt: string | null): string {
  const seen = `visto no LinkedIn em ${formatDateOnly(observedAt)}`;
  if (!observedBeforeAt) return seen;
  return `${seen}, entre ${formatDateOnly(observedBeforeAt)} e ${formatDateOnly(observedAt)}`;
}

/** A mudança guardada no payload de um evento da timeline, ou null se não for uma. */
export function transitionFromEventPayload(
  eventType: string,
  payload: Record<string, unknown> | null,
): LinkedInTransition | null {
  const kind = TRANSITION_KIND_BY_EVENT_TYPE[eventType];
  if (!kind) return null;
  const source = payload ?? {};
  const asText = (value: unknown) => (typeof value === "string" ? value : undefined);
  const asChange = (value: unknown) =>
    typeof value === "string" || typeof value === "boolean" ? value : null;
  const detailsSource =
    source.detalhes && typeof source.detalhes === "object"
      ? (source.detalhes as Record<string, unknown>)
      : source;
  return {
    kind,
    antes: asChange(source.antes),
    depois: asChange(source.depois),
    detalhes: {
      empresa: asText(detailsSource.empresa),
      cargo: asText(detailsSource.cargo),
      vinculo: asText(detailsSource.vinculo),
      data_inicio: asText(detailsSource.data_inicio),
      data_fim: asText(detailsSource.data_fim),
      primeira_observacao: detailsSource.primeira_observacao === true,
      provedores_diferentes: detailsSource.provedores_diferentes === true,
    },
  };
}
